import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PaletteSidebar from './components/PaletteSideBar';
import FileBrowser from './components/FileBrowser';
import TabManager from './components/TabManager';
import AppToolbar from './components/Toolbar';
import PropertiesPanel from './components/PropertiesPanel';
import Sidebar from './components/Sidebar';
import SettingsPanel from './components/SettingsPanel';
import SchedulerPanel from './components/SchedulerPanel';
import MetadataView from './components/metadata/MetadataView';
import Dashboard from './components/monitoring/Dashboard';
import Documentation from './docs/Documentation';
import GlobalSearchModal from './components/GlobalSearch/GlobalSearchModal';
import NavigationTracker from './components/NavigationTracker';
import useAppInitialization from './hooks/useAppInitialization';
import './App.css';
import { Layout, ConfigProvider, theme as antdTheme, theme } from 'antd';
import 'antd/dist/reset.css';
import { debugStore, useStore, useFlowActions } from './store/store';
import { useLayoutStore } from './store/layoutStore';
// Initialize i18n
import './i18n';
// Authentication components
import { AuthProvider, ProtectedRoute, AuthCallback } from './components/auth';
// Theme system
import { ThemeProvider } from './contexts/ThemeContext';
import { useAntdTheme, useTheme } from './hooks/useTheme';
// Font size system
import { FontSizeProvider, useFontSize } from './contexts/FontSizeContext';
// Compact algorithm system
import { CompactAlgorithmProvider, useCompactAlgorithm } from './contexts/CompactAlgorithmContext';
// Dark mode system
import { DarkModeProvider, useDarkMode } from './contexts/DarkModeContext';

// Type definitions
type ViewType = 'palette' | 'files' | 'metadata' | 'dashboard' | 'settings' | 'scheduler' | 'welcome' | 'docs' | null;

interface CustomRouterProps {
  children: React.ReactNode;
}

// Extend window interface to include debugStore
declare global {
  interface Window {
    debugStore: typeof debugStore;
  }
}

// Create a custom router with the v7_startTransition flag
const CustomRouter: React.FC<CustomRouterProps> = ({ children }) => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <NavigationTracker />
      {children}
    </Router>
  );
};

// Themed wrapper component for Ant Design ConfigProvider
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentTheme } = useTheme();
  // Get font size preference - this will apply the saved font size on mount and update reactively
  const { fontSizeNumber } = useFontSize();
  // Get compact algorithm preference
  const { isCompactEnabled } = useCompactAlgorithm();
  // Get dark mode preference
  const { isDarkMode } = useDarkMode();
  
  // Memoize theme config to ensure it updates when dependencies change
  const themeConfig = useMemo(() => {
    // Build algorithms array based on user preferences
    const algorithms = [
      isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    ];
    
    // Conditionally add compact algorithm if enabled
    if (isCompactEnabled) {
      algorithms.push(antdTheme.compactAlgorithm);
    }
    
    return {
      token: {
        // Only set the primary color from the theme - let algorithms handle everything else
        colorPrimary: currentTheme.colors.primary,
        fontSize: fontSizeNumber,
      },
      algorithm: algorithms,
      components: {
        Menu: {
          itemBg: 'transparent',
          subMenuItemBg: 'transparent',
        },
        Tree: {
          indentSize: 12,
        },
      },
    };
  }, [currentTheme, fontSizeNumber, isCompactEnabled, isDarkMode]);
  
  return (
    <ConfigProvider theme={themeConfig}>
      {children}
    </ConfigProvider>
  );
};

// Main application component wrapped with authentication
const MainApp: React.FC = () => {
  const { token } = theme.useToken();
  const [activeView, setActiveView] = useState<ViewType>('palette');
  const [showWelcome, setShowWelcome] = useState(true);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [selectedDocPage, setSelectedDocPage] = useState<string>('getting-started');

  // Layout store for docking
  const { propertiesPanelDock, propertiesPanelSize, setPropertiesPanelSize } = useLayoutStore();
  
  // Get properties panel visibility state
  const { selectedNode, isDoubleClicked } = useStore((state) => {
    const activePipeline = state.pipelines.find((p) => p.id === state.activePipelineId);
    const selectedNode = activePipeline?.nodes.find((n) => n.id === activePipeline?.selectedNodeId);
    return {
      selectedNode,
      isDoubleClicked: activePipeline?.isNodeDoubleClicked || false,
    };
  });

  // Determine if properties panel should be visible
  const isPanelVisible = selectedNode && isDoubleClicked;

  // Get flow actions for cleanup
  const { clearNodeSelectionState } = useFlowActions();

  useAppInitialization();

  // Cleanup node selection state on page load and when active pipeline changes
  useEffect(() => {
    // Clear any stale node selection state on page load
    clearNodeSelectionState();
  }, [clearNodeSelectionState]);

  // Global keyboard shortcut for search (Cmd/Ctrl + K) and toolbar button event
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchModalOpen(prev => !prev); // Toggle the modal
      }
    };

    const handleOpenSearchEvent = () => {
      setSearchModalOpen(true);
    };

    const handleNavigateToFolder = (event: CustomEvent) => {
      const { folderPath } = event.detail;
      setActiveView('files');
      // Dispatch event to FileBrowser to expand the folder
      window.dispatchEvent(new CustomEvent('expandFolder', { detail: { folderPath } }));
    };

    const handleNavigateToDocumentation = (event: CustomEvent) => {
      const { pageId } = event.detail;
      setActiveView('docs');
      setSelectedDocPage(pageId);
      setShowWelcome(false); // Hide welcome screen
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('openGlobalSearch', handleOpenSearchEvent);
    window.addEventListener('navigateToFolder', handleNavigateToFolder as EventListener);
    window.addEventListener('navigateToDocumentation', handleNavigateToDocumentation as EventListener);
    
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('openGlobalSearch', handleOpenSearchEvent);
      window.removeEventListener('navigateToFolder', handleNavigateToFolder as EventListener);
      window.removeEventListener('navigateToDocumentation', handleNavigateToDocumentation as EventListener);
    };
  }, []);

  const handleViewChange = (key: string | null) => {
    setActiveView(key as ViewType);
  };

  return (
    <Layout style={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
        <AppToolbar />
        <Layout style={{ flex: 1, overflow: 'hidden' }}>
          <Sidebar onViewChange={handleViewChange} activeView={activeView} />
          <Layout style={{ flex: 1, overflow: 'hidden' }}>
            {activeView === 'settings' ? (
              <SettingsPanel />
            ) : activeView === 'dashboard' ? (
              <Dashboard />
            ) : activeView === 'scheduler' ? (
              <SchedulerPanel visible={true} onClose={() => setActiveView(null)} />
            ) : activeView === 'docs' ? (
              <Documentation initialPage={selectedDocPage} />
            ) : (
              <>
                <div 
                  style={{ 
                    display: 'flex', 
                    flex: 1, 
                    overflow: 'hidden',
                    flexDirection: propertiesPanelDock === 'bottom' ? 'column' : 'row'
                  }}
                >
                  {/* Main horizontal layout */}
                  <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {/* Left sidebar content */}
                    {activeView === 'palette' && <PaletteSidebar />}
                    {activeView === 'files' && <FileBrowser />}
                    {activeView === 'metadata' && <MetadataView />}

                    {/* Main content area */}
                    <div
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <TabManager showWelcome={showWelcome} onShowWelcomeChange={setShowWelcome} />
                      
                      {/* Floating properties panel (when not docked) */}
                      {propertiesPanelDock === 'floating' && <PropertiesPanel />}
                    </div>

                    {/* Right docked properties panel */}
                    {propertiesPanelDock === 'right' && isPanelVisible && (
                      <div 
                        style={{ 
                          width: propertiesPanelSize.width,
                          borderLeft: '1px solid',
                          flexShrink: 0,
                          position: 'relative'
                        }}
                      >
                        {/* Left resize handle for right panel */}
                        <div
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: '6px',
                            cursor: 'ew-resize',
                            zIndex: 1000,
                            backgroundColor: 'transparent',
                            borderLeft: '1px solid transparent',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const startX = e.clientX;
                            const startWidth = propertiesPanelSize.width;

                            const handleMouseMove = (e: MouseEvent) => {
                              const newWidth = startWidth - (e.clientX - startX);
                              const minWidth = 300;
                              const maxWidth = window.innerWidth * 0.6;
                              
                              if (newWidth >= minWidth && newWidth <= maxWidth) {
                                setPropertiesPanelSize({ ...propertiesPanelSize, width: newWidth });
                              }
                            };

                            const handleMouseUp = () => {
                              document.removeEventListener('mousemove', handleMouseMove);
                              document.removeEventListener('mouseup', handleMouseUp);
                            };

                            document.addEventListener('mousemove', handleMouseMove);
                            document.addEventListener('mouseup', handleMouseUp);
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e6f7ff';
                            e.currentTarget.style.borderLeft = '2px solid #1890ff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.borderLeft = '1px solid transparent';
                          }}
                        />
                        <PropertiesPanel />
                      </div>
                    )}
                  </div>

                  {/* Bottom docked properties panel */}
                  {propertiesPanelDock === 'bottom' && isPanelVisible && (
                    <div 
                      style={{ 
                        height: propertiesPanelSize.height,
                        borderTop: '1px solid',
                        flexShrink: 0,
                        position: 'relative'
                      }}
                    >
                      {/* Top resize handle for bottom panel */}
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          right: 0,
                          height: '6px',
                          cursor: 'ns-resize',
                          zIndex: 1000,
                          backgroundColor: 'transparent',
                          borderTop: '1px solid transparent',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startY = e.clientY;
                          const startHeight = propertiesPanelSize.height;

                          const handleMouseMove = (e: MouseEvent) => {
                            const newHeight = startHeight - (e.clientY - startY);
                            const minHeight = 200;
                            const maxHeight = window.innerHeight * 0.6;
                            
                            if (newHeight >= minHeight && newHeight <= maxHeight) {
                              setPropertiesPanelSize({ ...propertiesPanelSize, height: newHeight });
                            }
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener('mousemove', handleMouseMove);
                            document.removeEventListener('mouseup', handleMouseUp);
                          };

                          document.addEventListener('mousemove', handleMouseMove);
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#e6f7ff';
                          e.currentTarget.style.borderTop = '2px solid #1890ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderTop = '1px solid transparent';
                        }}
                      />
                      <PropertiesPanel />
                    </div>
                  )}
                </div>
              </>
            )}
          </Layout>
        </Layout>
        <GlobalSearchModal
          open={searchModalOpen}
          onClose={() => setSearchModalOpen(false)}
        />
      </Layout>
  );
};

// Main App component with authentication provider and routing
const App: React.FC = () => {
  return (
        <ThemeProvider>
          <DarkModeProvider>
            <FontSizeProvider>
              <CompactAlgorithmProvider>
                <ThemedApp>
          <AuthProvider>
            <CustomRouter>
              <Routes>
                {/* Authentication callback route - NOT protected, accessible during auth flow */}
                <Route path="/auth/callback" element={<AuthCallback />} />
                
                {/* Main application route - protected by authentication */}
                <Route 
                  path="/*" 
                  element={
                    <ProtectedRoute>
                      <MainApp />
                    </ProtectedRoute>
                  } 
                />
              </Routes>
            </CustomRouter>
          </AuthProvider>
                </ThemedApp>
              </CompactAlgorithmProvider>
            </FontSizeProvider>
          </DarkModeProvider>
        </ThemeProvider>
  );
};

export default App;
